import type { CloudProvider } from "./__generated__/CloudOrganizationCard_organization.graphql";

/** Path of the identity providers list page. */
export const IDENTITY_PROVIDERS_PATH =
  "/settings/access-control/identity-providers";

/**
 * Tooltip copy for the add affordances — the list page's action bar and the add
 * form's save action — which are disabled without system edit permission.
 */
export const ADD_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to add an identity provider";

/**
 * Path of a SAML provider's details page. SAML providers are addressed by their
 * Cognito provider name, which is unique and immutable but caller-supplied, so
 * it is encoded rather than interpolated raw.
 *
 * The kind is part of the path because the two kinds of provider are addressed
 * differently — a SAML provider by name, a cloud organization by provider and
 * key — and neither identifier space can be told from the other on sight.
 */
export function samlProviderPath(name: string) {
  return `${IDENTITY_PROVIDERS_PATH}/saml/${encodeURIComponent(name)}`;
}

/** Path of a SAML provider's edit form. */
export function samlProviderEditPath(name: string) {
  return `${samlProviderPath(name)}/edit`;
}

/** A cloud provider the console knows, excluding Relay's forward-compat sentinel. */
type KnownCloudProvider = Exclude<CloudProvider, "%future added value">;

/**
 * The URL segment naming each cloud provider, and the single source of truth
 * for the mapping in both directions. Keyed by the enum rather than by the
 * segment, so a provider added to the schema is a missing-property error here
 * rather than a link whose details route silently fails to match.
 */
const PROVIDER_SEGMENTS: Record<KnownCloudProvider, string> = {
  AWS: "aws",
  Azure: "azure",
  GCP: "gcp",
  Kubernetes: "kubernetes",
  TencentCloud: "tencentcloud",
};

/** The inverse of {@link PROVIDER_SEGMENTS}, for reading a segment back. */
const SEGMENT_PROVIDERS = new Map(
  Object.entries(PROVIDER_SEGMENTS).map(([provider, segment]) => [
    segment,
    provider as KnownCloudProvider,
  ]),
);

/**
 * Path of a cloud organization's details page. Organizations are addressed by
 * the (provider, key) pair that identifies them in the API; the provider is
 * written as its URL segment and mapped back by {@link cloudProviderFromParam}.
 *
 * A provider the schema has but this map does not — which the type keyed on
 * `CloudProvider` above is there to prevent — falls back to the lower-cased
 * enum value, so the link still says which provider it meant.
 */
export function cloudOrganizationPath(provider: CloudProvider, key: string) {
  const segment =
    PROVIDER_SEGMENTS[provider as KnownCloudProvider] ?? provider.toLowerCase();

  return `${IDENTITY_PROVIDERS_PATH}/organization/${segment}/${encodeURIComponent(key)}`;
}

/**
 * Reads the `:provider` route param back into a `CloudProvider`, or null if the
 * segment names none.
 */
export function cloudProviderFromParam(param: string | undefined) {
  return param ? (SEGMENT_PROVIDERS.get(param.toLowerCase()) ?? null) : null;
}

/**
 * Tooltip copy for the edit affordances — the details page's edit action and
 * the edit form's save action — which are disabled without system edit
 * permission.
 */
export const EDIT_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to edit an identity provider";

/**
 * Tooltip copy for the details page's sync action, which is disabled without
 * system edit permission.
 */
export const SYNC_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to sync an identity provider";

/**
 * Tooltip copy for the details page's remove action, which is disabled without
 * system edit permission.
 */
export const REMOVE_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to remove an identity provider";
